// src/components/gatherings/ScheduleInfoSection.tsx
import React from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Chip, 
  Button,
  Tooltip,
  Divider
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid directly as per MUI v7 pattern
import { format, parseISO } from 'date-fns';
import { Gathering } from '../../../../types';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

interface ScheduleInfoSectionProps {
  gathering: Gathering;
  onReschedule: () => void;
}

const ScheduleInfoSection: React.FC<ScheduleInfoSectionProps> = ({
  gathering,
  onReschedule
}) => {
  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (timeString: string): string => {
    if (!timeString) return 'N/A';
    
    try {
      // Check if the time already has AM/PM
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }
      
      // Assuming time is in format like "19:00:00"
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${formattedHour}:${minutes} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} mins`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    } else {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes} mins`;
    }
  };

  return (
    <Paper 
      elevation={0} 
      variant="outlined" 
      sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 2,
        position: 'relative'
      }}
    >
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
        Schedule Information
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {/* Date and Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">Date</Typography>
            </Box>
            <Typography variant="body1" fontWeight="medium">
              {formatDate(gathering.date)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Start Time</Typography>
              </Box>
              <Typography variant="body1">
                {formatTime(gathering.start_time)}
                {gathering.space?.timezone && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({gathering.space.timezone})
                  </Typography>
                )}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">End Time</Typography>
              </Box>
              <Typography variant="body1">
                {formatTime(gathering.end_time)}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
              </Box>
              <Typography variant="body1">
                {formatDuration(gathering.duration)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Space and Areas */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Space */}
          {gathering.space && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Space</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Tooltip 
                  title={gathering.space.address ? 
                    `${gathering.space.address.address1}${gathering.space.address.address2 ? `, ${gathering.space.address.address2}` : ''}
                    ${gathering.space.address.city}, ${gathering.space.address.state} ${gathering.space.address.zip_code}
                    ${gathering.space.address.country}` : ''
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={gathering.space.name}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: 'primary.light' }}
                  />
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Areas */}
          {gathering.areas && gathering.areas.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MeetingRoomIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                <Typography variant="subtitle2" color="text.secondary">Areas</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {gathering.areas.map((area) => (
                  <Tooltip
                    key={area.id}
                    title={
                      <>
                        {area.description && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {area.description}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Capacity: {area.min_capacity > 0 ? `${area.min_capacity}-${area.capacity}` : area.capacity} people
                        </Typography>
                      </>
                    }
                    arrow
                    placement="top"
                  >
                    <Chip
                      label={area.name}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: 'primary.light' }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ScheduleInfoSection;
